import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { BUSINESS } from "@/data/config";

// Kinetic hero: masked line by line reveal on load, subtle image parallax on scroll.
const heroLines = [
  ["Seamless gutters", "cut on site,"],
  ["hung the same day,"],
  ["built for Spokane snow."],
];

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1778856951796-d1192dab6d54?crop=entropy&cs=srgb&fm=jpg&w=1600";

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 140]);
  const overlayY = useTransform(scrollY, [0, 800], [0, -80]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[92vh] flex items-end pb-16 md:pb-24 overflow-hidden"
    >
      {/* Right column image with parallax */}
      <motion.div
        style={{ y: imgY }}
        className="hidden md:block absolute top-0 right-0 h-full md:w-[52%] lg:w-[46%]"
      >
        <div className="relative h-full w-full">
          <img
            src={HERO_IMAGE}
            alt="A modern home in Spokane with rain running off the roof line"
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover clipped-image"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-ink-950/40 to-ink-950 clipped-image" />
          <div className="absolute inset-0 bg-black/25 clipped-image" />
        </div>
      </motion.div>

      {/* Mobile: subtle top gradient only, no photo behind headline */}
      <div className="md:hidden absolute top-0 inset-x-0 h-[40vh] bg-gradient-to-b from-copper-500/[0.08] to-transparent pointer-events-none" />

      {/* Grid + horizontal gutter lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="max-w-[1440px] mx-auto h-full px-5 md:px-10 relative">
          <div className="absolute top-[20%] left-0 right-0 h-px bg-white/5" />
          <div className="absolute top-[50%] left-0 right-0 h-px bg-white/5" />
          <div className="absolute top-[80%] left-0 right-0 h-px bg-white/5" />
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-5 md:px-10 relative z-10 w-full">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="overline mb-6 md:mb-10 text-[0.65rem] md:text-xs"
              data-testid="hero-overline"
            >
              Spokane WA · Family owned since {BUSINESS.founded}
            </motion.p>

            <h1
              className="font-display text-[14vw] sm:text-[11vw] md:text-[8vw] lg:text-[7.4vw] xl:text-[6.6vw] leading-[0.92] tracking-tightest text-bone-50 max-w-[16ch]"
              data-testid="hero-headline"
            >
              {heroLines.map((line, li) => (
                <span key={li} className="mask-wrap">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{
                      duration: 1.05,
                      ease: [0.76, 0, 0.24, 1],
                      delay: 0.25 + li * 0.14,
                    }}
                    className="block"
                  >
                    {line.map((word, wi) => (
                      <span key={wi} className={wi === line.length - 1 && li === 2 ? "text-copper-500" : ""}>
                        {word}{" "}
                      </span>
                    ))}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.6 }}
              className="mt-10 md:mt-14 max-w-xl"
            >
              <p className="text-bone-300 text-lg md:text-xl leading-relaxed mb-8">
                Family owned crew serving Spokane, Spokane Valley, Liberty Lake, Mead, Cheney, and Nine Mile Falls. On site quotes. Same day installs. One year workmanship warranty.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center justify-between gap-3 bg-copper-500 hover:bg-copper-600 text-ink-950 hover:text-white font-heading font-semibold text-base px-6 sm:px-7 py-4 rounded-sm transition-colors duration-200"
                >
                  Request a Free Quote
                  <ArrowUpRight className="w-4 h-4 shrink-0 group-hover:rotate-45 transition-transform duration-300" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center sm:justify-between gap-3 border border-white/15 hover:border-copper-500 text-bone-50 font-heading font-semibold text-base px-6 sm:px-7 py-4 rounded-sm transition-colors duration-200 whitespace-nowrap"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  {BUSINESS.phone}
                </a>
              </div>
            </motion.div>
          </div>

          <motion.div
            style={{ y: overlayY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="hidden md:block col-span-4 self-end"
          >
            <div className="border border-white/10 bg-ink-950/70 backdrop-blur-sm rounded-sm p-6">
              <div className="overline mb-4">On the roof this week</div>
              <ul className="space-y-4 text-sm text-bone-300">
                <li className="flex items-baseline gap-3">
                  <span className="text-copper-500 font-display text-xl leading-none">01</span>
                  Ice dam repair, South Hill Spokane
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-copper-500 font-display text-xl leading-none">02</span>
                  Full seamless install, Liberty Lake
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-copper-500 font-display text-xl leading-none">03</span>
                  Micro mesh guards, Mead
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
